/** Parsed geo_score_json structure */
export interface GeoScoreDimension {
  score: number;
  notes?: string;
}

export interface ArticleGeoScore {
  overall_score: number;
  structural_compliance?: GeoScoreDimension;
  entity_density?: GeoScoreDimension;
  keyword_coverage?: GeoScoreDimension;
  readability?: GeoScoreDimension;
  direct_answer_quality?: GeoScoreDimension;
  faq_completeness?: GeoScoreDimension;
  authority_signals?: GeoScoreDimension;
  ai_extractability?: GeoScoreDimension;
  top_improvements?: string[];
  [key: string]: unknown;
}

const SCORE_DIMENSIONS = [
  { key: 'structural_compliance', label: 'Structure' },
  { key: 'entity_density', label: 'Entities' },
  { key: 'keyword_coverage', label: 'Keywords' },
  { key: 'readability', label: 'Readability' },
  { key: 'direct_answer_quality', label: 'Direct Answer' },
  { key: 'faq_completeness', label: 'FAQ Quality' },
  { key: 'authority_signals', label: 'Authority' },
  { key: 'ai_extractability', label: 'AI Extractability' },
] as const;

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-400';
  if (score >= 60) return 'text-yellow-300';
  return 'text-red-400';
}

function scoreBgColor(score: number): string {
  if (score >= 80) return 'bg-green-400';
  if (score >= 60) return 'bg-yellow-300';
  return 'bg-red-400';
}

function scoreBorderColor(score: number): string {
  if (score >= 80) return 'border-green-400';
  if (score >= 60) return 'border-yellow-300';
  return 'border-red-400';
}

interface ArticleGeoScoreTabProps {
  geoScore: ArticleGeoScore;
}

export function ArticleGeoScoreTab({ geoScore }: ArticleGeoScoreTabProps) {
  return (
    <div className="bg-slate-800 rounded-[10px] p-6 border border-slate-700">
      {/* Overall score */}
      <div className="text-center mb-8">
        <div
          className={`inline-flex items-center justify-center w-[120px] h-[120px] rounded-full bg-slate-900 border-4 ${scoreBorderColor(geoScore.overall_score)}`}
        >
          <div>
            <div className="text-4xl font-bold text-slate-100">{geoScore.overall_score}</div>
            <div className="text-[11px] text-slate-500 -mt-1">/ 100</div>
          </div>
        </div>
        <p className="text-slate-400 mt-3 text-sm">Overall GEO Score</p>
      </div>

      {/* Dimension scores */}
      <div className="grid grid-cols-2 gap-3">
        {SCORE_DIMENSIONS.map(dim => {
          const data = geoScore[dim.key] as GeoScoreDimension | undefined;
          if (!data) return null;
          const score = data.score || 0;
          return (
            <div key={dim.key} className="bg-slate-900 rounded-lg p-3.5 border border-slate-700">
              <div className="flex justify-between mb-1.5">
                <span className="text-slate-200 text-[13px] font-medium">{dim.label}</span>
                <span className={`font-bold ${scoreColor(score)}`}>{score}</span>
              </div>
              <div className="h-1 bg-slate-700 rounded-sm">
                <div
                  className={`h-full rounded-sm ${scoreBgColor(score)}`}
                  style={{ width: `${score}%` }}
                />
              </div>
              <p className="text-slate-500 text-[11px] mt-1.5 mb-0">{data.notes}</p>
            </div>
          );
        })}
      </div>

      {/* Improvements */}
      {geoScore.top_improvements && geoScore.top_improvements.length > 0 && (
        <div className="mt-6">
          <h4 className="text-slate-100 mb-2.5 text-[15px]">Suggested Improvements</h4>
          <ol className="m-0 pl-[18px] text-slate-400">
            {geoScore.top_improvements.map((imp: string, i: number) => (
              <li key={i} className="mb-1.5 leading-normal text-[13px]">{imp}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
